import { Select, SelectItem } from '../Carbon';
import React from 'react';
import { getTimezoneList } from '../../constants/constants';
import { getRandomId } from '../../helpers/helpers';
import { language } from '../../services/LanguageService';
import '../../styles/components/AsperaTimePicker.scss';
import { AsperaError } from '../../classes/AsperaError';
import { deepClone } from '../../helpers/starterHelpers';
/**
 * React Component
 *
 * The AsperaTimePicker component renders a time input form based on styling from Carbon
 *
 * @example
 * ```javascript
 * <AsperaTimePicker onChange={this.onChange} showTimezone={true} />
 * ```
 */
export class AsperaTimePicker extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
        };
        this.currentTime = {
            hour: 0,
            minute: 0,
        };
        this.currentPm = false;
        this.parseTimeZoneString = (time) => {
            const isNegative = time < 0;
            time = Math.abs(time);
            const hours = time / 60;
            const floorHours = Math.floor(hours);
            const minutes = Math.round((hours - floorHours) * 60);
            return `GMT${isNegative ? '-' : '+'}${this.getPaddedTime(floorHours, minutes)}`;
        };
        this.triggerOnChange = () => {
            const { onChange } = this.props;
            this.currentTime.timezoneString = this.parseTimeZoneString(this.currentTime.timezone || 0);
            if (typeof onChange === 'function')
                onChange(Object.assign({}, this.currentTime));
            this.setState({});
        };
        this.getPaddedNumber = (item) => {
            return `${item < 10 ? `0${item}` : item}`;
        };
        this.getPaddedTime = (hour, minute) => {
            let showHour = (hour !== undefined ? hour : this.currentTime.hour) || 0;
            const showMinute = (minute !== undefined ? minute : this.currentTime.minute) || 0;
            if (this.showAmPm && hour === undefined) {
                if (this.currentPm && showHour > 12) {
                    showHour = showHour - 12;
                }
                else if (showHour === 0) {
                    showHour = 12;
                }
            }
            return `${this.getPaddedNumber(showHour)}:${this.getPaddedNumber(showMinute)}`;
        };
        this.setupFromValue = () => {
            const { initialValue } = this.props;
            this.setState({ loading: true });
            if (initialValue) {
                this.currentTime = deepClone(initialValue);
                if (this.showAmPm && this.currentTime.hour > 11) {
                    this.currentPm = true;
                }
            }
            if (this.currentTime.timezone === undefined) {
                this.currentTime.timezone = this.localTimezoneOffset || 0;
            }
            this.setState({}, () => {
                this.setState({ loading: false });
            });
        };
    }
    get showAmPm() {
        const { showAmPm } = this.props;
        if (typeof showAmPm === 'boolean') {
            return showAmPm;
        }
        else {
            return !language.getCurrentLanguage().timeFormat24;
        }
    }
    getAmPmField(id) {
        const { disabled } = this.props;
        let defaultValue = 'AM';
        if (this.currentTime.hour > 11) {
            defaultValue = 'PM';
            this.currentPm = true;
        }
        return (React.createElement(Select, { labelText: '', disabled: disabled, className: "aspera-time-picker--select", value: defaultValue, id: `aspera-time-picker-am-pm-${id}`, onChange: (event) => {
                if (event.target.value === 'PM' && this.currentTime.hour < 12) {
                    this.currentTime.hour += 12;
                }
                else if (event.target.value === 'AM' && this.currentTime.hour >= 12) {
                    this.currentTime.hour -= 12;
                }
                this.currentPm = event.target.value === 'PM';
                this.triggerOnChange();
            }, "data-auto": "aspera-time-picker-am-pm" },
            React.createElement(SelectItem, { value: 'AM', text: language.get('timePicker.am') }),
            React.createElement(SelectItem, { value: 'PM', text: language.get('timePicker.pm') })));
    }
    getTimezoneField(id) {
        const { disabled } = this.props;
        return (React.createElement(Select, { labelText: '', disabled: disabled, className: "aspera-time-picker--select timezone", value: String(this.currentTime.timezone), id: `aspera-time-picker-timezone-${id}`, onChange: (event) => {
                this.currentTime.timezone = Number(event.target.value);
                this.triggerOnChange();
            }, "data-auto": "aspera-time-picker-timezone" }, getTimezoneList().map((item, index) => {
            return React.createElement(SelectItem, { key: index, value: String(item.offset), text: item.name });
        })));
    }
    getHourItem(id) {
        const { required, disabled } = this.props;
        const hours = this.showAmPm ? [12] : [];
        for (let i = (this.showAmPm ? 1 : 0); i <= (this.showAmPm ? 11 : 23); i++) {
            hours.push(i);
        }
        let finalValue = this.currentTime.hour;
        if (this.showAmPm) {
            if (this.currentTime.hour === 0) {
                finalValue = 12;
            }
            else if (this.currentTime.hour > 12) {
                finalValue = this.currentTime.hour - 12;
            }
        }
        return (React.createElement(Select, { disabled: disabled, className: "aspera-time-picker--text time-item", value: String(finalValue), labelText: '', required: required, id: `aspera-time-picker-hour-${id}`, onChange: (event) => {
                const hourValue = Number(event.target.value);
                if (!isNaN(hourValue)) {
                    if (this.showAmPm) {
                        if (this.currentPm) {
                            if (hourValue !== 12) {
                                this.currentTime.hour = hourValue + 12;
                            }
                            else {
                                this.currentTime.hour = hourValue;
                            }
                        }
                        else {
                            if (hourValue === 12) {
                                this.currentTime.hour = 0;
                            }
                            else {
                                this.currentTime.hour = hourValue;
                            }
                        }
                    }
                    else {
                        this.currentTime.hour = hourValue;
                    }
                }
                else {
                    new AsperaError('AsperaTimePicker: hour was not a number', { hourValue });
                    this.currentTime.hour = 0;
                }
                this.triggerOnChange();
            } }, hours.map((item, index) => {
            return React.createElement(SelectItem, { key: index, value: String(Number(item)), text: this.getPaddedNumber(item), "data-auto": `aspera-time-picker-hour-${id}-${item}` });
        })));
    }
    getMinuteItem(id) {
        const { required, disabled } = this.props;
        const minutes = [];
        for (let i = 0; i < 60; i++) {
            minutes.push(i);
        }
        return (React.createElement(Select, { disabled: disabled, className: "aspera-time-picker--text time-item no-left-border", value: String(this.currentTime.minute), labelText: '', required: required, id: `aspera-time-picker-minute-${id}`, onChange: (event) => {
                const minuteValue = Number(event.target.value);
                if (!isNaN(minuteValue)) {
                    this.currentTime.minute = minuteValue;
                }
                else {
                    new AsperaError('AsperaTimePicker: minute was not a number', { minuteValue });
                    this.currentTime.minute = 0;
                }
                this.triggerOnChange();
            } }, minutes.map((item, index) => {
            return React.createElement(SelectItem, { key: index, value: String(Number(item)), text: this.getPaddedNumber(item), "data-auto": `aspera-time-picker-minute-${id}-${item}` });
        })));
    }
    /**
     * Check if current Date (from local) is in daylight savings and adjust.
     * getTimezoneOffset returns positive for items behind and negative for items ahead
     */
    get localTimezoneOffset() {
        const now = new Date();
        const winter = new Date(now.getFullYear(), 0, 1);
        const summer = new Date(now.getFullYear(), 6, 1);
        const offset = Math.max(winter.getTimezoneOffset(), summer.getTimezoneOffset());
        return offset * -1;
    }
    componentDidUpdate(prevProps) {
        var _a, _b, _c, _d;
        const { initialValue } = this.props;
        if ((initialValue === null || initialValue === void 0 ? void 0 : initialValue.hour) !== ((_a = prevProps === null || prevProps === void 0 ? void 0 : prevProps.initialValue) === null || _a === void 0 ? void 0 : _a.hour) ||
            (initialValue === null || initialValue === void 0 ? void 0 : initialValue.minute) !== ((_b = prevProps === null || prevProps === void 0 ? void 0 : prevProps.initialValue) === null || _b === void 0 ? void 0 : _b.minute) ||
            (initialValue === null || initialValue === void 0 ? void 0 : initialValue.timezone) !== ((_c = prevProps === null || prevProps === void 0 ? void 0 : prevProps.initialValue) === null || _c === void 0 ? void 0 : _c.timezone) ||
            (initialValue === null || initialValue === void 0 ? void 0 : initialValue.timezoneString) !== ((_d = prevProps === null || prevProps === void 0 ? void 0 : prevProps.initialValue) === null || _d === void 0 ? void 0 : _d.timezoneString)) {
            this.setupFromValue();
        }
    }
    componentDidMount() {
        this.setupFromValue();
    }
    render() {
        const { className, id, showTimezone, labelText, helperText, light, dataAuto } = this.props;
        const { loading } = this.state;
        const idToUse = id || getRandomId();
        if (loading) {
            return null;
        }
        return (React.createElement("div", { id: idToUse, className: `aspera-time-picker ${className ? className : ''} ${light ? 'light-mode' : ''}`, "data-auto": dataAuto },
            React.createElement("label", { htmlFor: `aspera-time-picker-text-${idToUse}`, className: "cds--label" }, labelText),
            React.createElement("div", { className: "aspera-time-picker--wrapper" },
                this.getHourItem(idToUse),
                React.createElement("span", { className: "time-split cds--select-input" }, ":"),
                this.getMinuteItem(idToUse),
                this.showAmPm && this.getAmPmField(idToUse),
                showTimezone && this.getTimezoneField(idToUse)),
            !!helperText && React.createElement("div", { className: "cds--form__helper-text" }, helperText)));
    }
}
